import { Dispatch, FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { hasNonDemoAccountPendingReferral } from '../../lib/store/account-flow/selectors';
import { getReferralOwnProfile as getReferralOwnProfileCall } from '../../lib/store/crm';
import { CRMCall } from '../../lib/store/crm/types';
import { isCallStatusError, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import { GlobalClassesEnum } from '../../assets/scss/globalClasses/globalClasses.constants';
import {
  CommissionsTabContent,
  Grid,
  Preloader,
  ReferralBalanceCard,
  ReferralPromoBanner,
  ReferralShareModal,
  ReferralsLockedBanner,
  ReferralsTabContent,
} from '../../components';
import NavButtons from '../../components/NavigationButtons/NavButtons';
import { useIsMobile } from '../../hooks';

import { REFERRALS_PAGE_SIZE } from './Referrals.constants';
import { ReferralsTabEnum } from './Referrals.types';
import useReferrals from './useReferrals';

import styles from './Referrals.module.scss';

const pageSubtitleClasses = classNames(
  GlobalClassesEnum.CommonBody2Text,
  GlobalClassesEnum.CommonTextSecondary,
);

const Referrals: FC<ReduxProps> = ({
  getReferralOwnProfile,
  hasAccountPendingReferral,
  isReferralProfileLoading,
  isReferralProfileResolved,
  referralCommissions,
  referralProfile,
  referralQrCode,
  referredUsers,
}: ReduxProps): JSX.Element => {
  const { isMobile, isTablet } = useIsMobile();

  const [activeTab, setActiveTab] = useState(ReferralsTabEnum.referrals);
  const [visibleCount, setVisibleCount] = useState(REFERRALS_PAGE_SIZE);
  const [isShareOpen, setIsShareOpen] = useState(false);

  useEffect(() => {
    getReferralOwnProfile();
  }, [getReferralOwnProfile]);

  const { commissions, hasMoreCommissions, hasMoreReferrals, referrals, summary } = useReferrals({
    referralCommissions,
    referralProfile,
    referredUsers,
    visibleCount,
  });

  if (isReferralProfileLoading && !isReferralProfileResolved) {
    return (
      <Grid className={styles.lockedGrid}>
        <div className={styles.preloaderWrapper}>
          <Preloader owner="Referrals" />
        </div>
      </Grid>
    );
  }

  const isAwaitingActivation = !referralProfile?.can_invite;

  if (isAwaitingActivation && !hasAccountPendingReferral) {
    return (
      <Grid className={styles.lockedGrid}>
        <ReferralsLockedBanner />
      </Grid>
    );
  }

  const hasMoreInActiveTab = activeTab === ReferralsTabEnum.commissions ? hasMoreCommissions : hasMoreReferrals;

  const tabs = [
    { text: lang.referralsTabReferrals(), component: <ReferralsTabContent referrals={referrals} /> },
    { text: lang.referralsTabCommissions(), component: <CommissionsTabContent commissions={commissions} /> },
  ];

  const balanceCard = <ReferralBalanceCard />;

  const pageIntroduction = (
    <div className={styles.introSection}>
      {!isMobile && <div className={GlobalClassesEnum.CommonH3Bold}>{lang.referralsTitle()}</div>}
      <div className={pageSubtitleClasses}>{lang.referralsSubtitle()}</div>
    </div>
  );

  const mainContent = (
    <div className={styles.container}>
      <ReferralPromoBanner
        summary={summary}
        onShare={() => setIsShareOpen(true)}
        isAwaitingActivation={isAwaitingActivation}
      />
      {(isMobile || isTablet) && balanceCard}
    </div>
  );

  const tabsContent = (
    <div className={styles.tabsSection}>
      <div className={styles.tabsPanel}>
        <NavButtons
          tabs={tabs}
          fullWidthButtons={isMobile || isTablet}
          sendCurrentTabIndex={(index: number) => setActiveTab(index)}
        />
        {hasMoreInActiveTab && (
          <button
            type="button"
            className={classNames(GlobalClassesEnum.CommonBody2Text, styles.loadMoreButton)}
            onClick={() => setVisibleCount((count) => count + REFERRALS_PAGE_SIZE)}
          >
            {lang.referralsLoadMore()}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Grid
        extraChildren={!isMobile && !isTablet ? balanceCard : null}
        notificationArea={pageIntroduction}
      >
        {mainContent}
      </Grid>
      <Grid>{tabsContent}</Grid>
      <ReferralShareModal
        isOpen={isShareOpen}
        link={summary.link}
        qrImageBase64={referralQrCode?.qr_image_base64}
        onClose={() => setIsShareOpen(false)}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasAccountPendingReferral: hasNonDemoAccountPendingReferral(state),
  isReferralProfileLoading: !isCallStatusReady(CRMCall.getReferralOwnProfile, state.crm.statusByCall)
    && !isCallStatusError(CRMCall.getReferralOwnProfile, state.crm.statusByCall),
  isReferralProfileResolved: state.crm.isReferralProfileResolved,
  referralCommissions: state.crm.referralCommissions,
  referralProfile: state.crm.referralProfile,
  referralQrCode: state.crm.referralQrCode,
  referredUsers: state.crm.referredUsers,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getReferralOwnProfile: () => dispatch(getReferralOwnProfileCall()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Referrals);
